import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { translationService } from "../../services/translationsService";
import {
  isI18nSourceDebugEnabled,
  markApiTranslations,
} from "../../utils/i18nSourceDebug";
import { DEFAULT_LANGUAGE_CODE } from "../../utils/languageUtils";
import {
  isPseudoLocaleEnabled,
  pseudoLocaliseResources,
} from "../../utils/pseudoLocale";
import { reportUnpublishedKeys } from "../../utils/reportUnpublishedKeys";
import { parsePortalTranslationsToResources } from "../../utils/translationsUtils";

export const PORTAL_TRANSLATIONS_QUERY_KEY = "portal-translations";

/**
 * EN-5191: loads the portal's own UI strings from the API and merges them into i18next.
 *
 * The bundle built into the app (`src/locales/en-GB.json`) is the baseline, so the portal
 * always renders — including the login screen, which nobody can get past if this request is
 * slow or the endpoint is down. What comes back from the API is layered on top of it, which
 * is what makes the API the source of truth for translated copy: a key present server-side
 * wins, a key missing server-side falls back to the shipped English.
 *
 * `overwrite: true` on addResourceBundle performs that layering: i18next's shallow merge is
 * `{...existing, ...published}`, which is exactly right for a flat bundle — a published key wins
 * and every key the API did not send survives. `deep` is deliberately left off: it only matters
 * for nested objects, and it merges INTO the existing object in place, which would overwrite the
 * imported `en-GB.json` module itself. Components re-render once the bundle lands because i18n is
 * initialised with `bindI18nStore: "added"`.
 */
export function usePortalTranslations() {
  // useTranslation (rather than the raw i18n singleton) so this hook re-runs when the
  // language changes and refetches for the new one.
  const { i18n } = useTranslation();
  const { language } = i18n;

  // The language whose bundle is actually in the i18next store. Not derived from `data`:
  // addResourceBundle runs in an effect, so there is one commit where the data has arrived but
  // the store has not been written yet, and rendering in that gap is the flash this prevents.
  const [appliedLanguage, setAppliedLanguage] = useState<string>();

  /**
   * Latched once the app has painted. The gate is about the FIRST paint — a signed-in user
   * must not watch English turn into Dutch as the page loads. Past that it has to stay open:
   * `appliedLanguage` only catches up inside the effect, so a mid-session switch in Settings
   * always has at least one render where it disagrees, even with the bundle already cached,
   * and closing the gate there replaces the whole route tree with a spinner. That unmounts the
   * page the user is standing on — unsaved edits, scroll position and open dropdowns go with
   * it, and LanguageSection finishes its own handler while unmounted.
   */
  const hasPainted = useRef(false);

  const { data, isError } = useQuery({
    queryKey: [PORTAL_TRANSLATIONS_QUERY_KEY, language],
    queryFn: async () => {
      const response =
        await translationService.getPortalTranslationsByLanguageCode(language);
      return parsePortalTranslationsToResources(response.data ?? []);
    },
    // The copy changes when someone publishes, not while a staff member is working, so this
    // does not need refetching for the lifetime of the tab.
    staleTime: Infinity,
    enabled: Boolean(language),
  });

  useEffect(() => {
    if (!data) return;

    // Before anything is transformed: the comparison is against the raw published set, and
    // pseudo-localising it first would make every key look translated.
    reportUnpublishedKeys(language, data);

    // Both aids transform only on the way into i18next, never the cached query data, so
    // toggling either off and on again cannot wrap a value twice. Pseudo runs first: the
    // source marker is what the highlighter looks for, so it has to be the outer one.
    const pseudo = isPseudoLocaleEnabled()
      ? pseudoLocaliseResources(data)
      : data;
    const bundle = isI18nSourceDebugEnabled()
      ? markApiTranslations(pseudo)
      : pseudo;

    i18n.addResourceBundle(language, "translation", bundle, false, true);
    setAppliedLanguage(language);
  }, [data, language, i18n]);

  /**
   * Whether the portal can render without showing the wrong language.
   *
   * en-GB needs nothing: its bundle is compiled into the build, so it is correct from the first
   * frame and the login screen must never be held behind a network request.
   *
   * Any other language has no bundle until this request lands — i18next would resolve through
   * fallbackLng and paint English, then repaint translated once the store is written. Holding
   * the first paint until the bundle is in is what stops a signed-in user watching their portal
   * change language under them.
   *
   * An error releases the gate: English is a worse experience than Dutch, but both are better
   * than a portal that never renders.
   */
  const isFirstPaintReady =
    language === DEFAULT_LANGUAGE_CODE ||
    appliedLanguage === language ||
    // Already in the store: a bundle cached from earlier in the session, or one some other
    // code path added. Nothing to wait for.
    i18n.hasResourceBundle(language, "translation") ||
    isError;

  if (isFirstPaintReady) {
    hasPainted.current = true;
  }

  const isPortalTranslationsReady = hasPainted.current || isFirstPaintReady;

  return { isPortalTranslationsError: isError, isPortalTranslationsReady };
}
